import { __decorate, __metadata } from "tslib";
import { IsUUIDWithLVS, nameOf } from '@goparrot/common';
import { ApiExtraModels, ApiProperty, ApiPropertyOptional, getSchemaPath, refs } from '@nestjs/swagger-v4';
import { Expose, Type } from 'class-transformer';
import { IsArray, IsEnum, IsOptional, ValidateNested } from 'class-validator';
import { ConfigurationExposeGroup, ConfigurationTypeEnum, NotifierProviderTypeEnum } from '../interface';
import { ReadAuxVariablesDto } from '../model/generated-dto/ReadAuxVariablesDto';
import { ReadCustomSubjectDto } from '../model/generated-dto/ReadCustomSubjectDto';
import { ReadDisabledActionsByChannelDto } from '../model/generated-dto/ReadDisabledActionsByChannelDto';
import { ReadMailStylingDto } from '../model/generated-dto/ReadMailStylingDto';
import { ReadConfigurationProviderDto } from './ReadConfigurationProviderDto';
import { ReadMailMailhogChannelConfigurationDto } from './ReadMailMailhogChannelConfigurationDto';
import { ReadMailPostMarkChannelConfigurationDto } from './ReadMailPostMarkChannelConfigurationDto';
import { ReadOneSignalConfigurationDto } from './ReadOneSignalConfigurationDto';
import { ReadSmsGoparrotChannelConfigurationDto } from './ReadSmsGoparrotChannelConfigurationDto';
import { ReadSmsNexmoChannelConfigurationDto } from './ReadSmsNexmoChannelConfigurationDto';
import { ReadSmsPlivoChannelConfigurationDto } from './ReadSmsPlivoChannelConfigurationDto';
import { ReadSmsSimpleTextingChannelConfigurationDto } from './ReadSmsSimpleTextingChannelConfigurationDto';
let ReadConfigurationDto = class ReadConfigurationDto {
};
__decorate([
    ApiProperty({ type: () => String, format: 'uuid', description: 'uuid v4', example: 'e12acc6f-2f34-442c-92d3-d4199fb12345' }),
    Expose({ groups: [ConfigurationExposeGroup.FIXTURE, ConfigurationExposeGroup.READ, ConfigurationExposeGroup.DELETE] }),
    __metadata("design:type", String)
], ReadConfigurationDto.prototype, "uuid", void 0);
__decorate([
    ApiProperty({ type: () => String, example: '4b3b2814-4d1e-4f0f-a243-5fb415c150e4' }),
    IsUUIDWithLVS('4', { always: true }),
    Expose({ groups: [ConfigurationExposeGroup.FIXTURE, ConfigurationExposeGroup.READ, ConfigurationExposeGroup.CREATE, ConfigurationExposeGroup.DELETE] }),
    __metadata("design:type", String)
], ReadConfigurationDto.prototype, "merchantId", void 0);
__decorate([
    ApiPropertyOptional({ type: () => String, example: '4b3b2814-4d1e-4f0f-a243-5fb415c150e4' }),
    IsUUIDWithLVS('4', { always: true }),
    Expose({
        groups: [
            ConfigurationExposeGroup.FIXTURE,
            ConfigurationExposeGroup.READ,
            ConfigurationExposeGroup.CREATE,
            ConfigurationExposeGroup.PATCH,
            ConfigurationExposeGroup.DELETE,
        ],
    }),
    __metadata("design:type", String)
], ReadConfigurationDto.prototype, "storeId", void 0);
__decorate([
    ApiPropertyOptional({ type: () => String, format: 'uuid', description: 'uuid v4', example: 'e12acc6f-2f34-442c-92d3-d4199fb12345' }),
    Expose({
        groups: [
            ConfigurationExposeGroup.FIXTURE,
            ConfigurationExposeGroup.CREATE,
            ConfigurationExposeGroup.READ,
            ConfigurationExposeGroup.PATCH,
            ConfigurationExposeGroup.DELETE,
        ],
    }),
    IsUUIDWithLVS('4', { always: true }),
    __metadata("design:type", String)
], ReadConfigurationDto.prototype, "parentConfigurationUuid", void 0);
__decorate([
    ApiPropertyOptional({ type: () => String, enum: ConfigurationTypeEnum, description: 'type of configuration', example: ConfigurationTypeEnum.CUSTOM }),
    Expose({ groups: [ConfigurationExposeGroup.FIXTURE, ConfigurationExposeGroup.CREATE, ConfigurationExposeGroup.READ, ConfigurationExposeGroup.DELETE] }),
    IsEnum(ConfigurationTypeEnum, { always: true }),
    IsOptional({ always: true }),
    __metadata("design:type", String)
], ReadConfigurationDto.prototype, "type", void 0);
__decorate([
    ApiPropertyOptional({ type: ReadOneSignalConfigurationDto }),
    Type(() => ReadOneSignalConfigurationDto),
    Expose({ groups: [ConfigurationExposeGroup.FIXTURE, ConfigurationExposeGroup.CREATE, ConfigurationExposeGroup.READ, ConfigurationExposeGroup.DELETE] }),
    ValidateNested({ always: true }),
    IsOptional({ always: true }),
    __metadata("design:type", ReadOneSignalConfigurationDto)
], ReadConfigurationDto.prototype, "notification", void 0);
__decorate([
    ApiPropertyOptional({ type: () => ReadMailStylingDto }),
    Type(() => ReadMailStylingDto),
    Expose({ groups: [ConfigurationExposeGroup.FIXTURE, ConfigurationExposeGroup.CREATE, ConfigurationExposeGroup.READ, ConfigurationExposeGroup.DELETE] }),
    ValidateNested({ always: true }),
    IsOptional({ always: true }),
    __metadata("design:type", ReadMailStylingDto)
], ReadConfigurationDto.prototype, "mailStyling", void 0);
__decorate([
    ApiPropertyOptional({ type: ReadAuxVariablesDto }),
    Type(() => ReadAuxVariablesDto),
    IsArray({ always: true }),
    Expose({ groups: [ConfigurationExposeGroup.FIXTURE, ConfigurationExposeGroup.CREATE, ConfigurationExposeGroup.READ, ConfigurationExposeGroup.DELETE] }),
    ValidateNested({ always: true, each: true }),
    IsOptional({ always: true }),
    __metadata("design:type", Array)
], ReadConfigurationDto.prototype, "auxVariables", void 0);
__decorate([
    ApiPropertyOptional({ type: ReadCustomSubjectDto }),
    Type(() => ReadCustomSubjectDto),
    IsArray({ always: true }),
    Expose({ groups: [ConfigurationExposeGroup.FIXTURE, ConfigurationExposeGroup.CREATE, ConfigurationExposeGroup.READ, ConfigurationExposeGroup.DELETE] }),
    ValidateNested({ always: true, each: true }),
    IsOptional({ always: true }),
    __metadata("design:type", Array)
], ReadConfigurationDto.prototype, "customSubject", void 0);
__decorate([
    ApiPropertyOptional({ type: ReadDisabledActionsByChannelDto }),
    Type(() => ReadDisabledActionsByChannelDto),
    Expose({ groups: [ConfigurationExposeGroup.FIXTURE, ConfigurationExposeGroup.CREATE, ConfigurationExposeGroup.READ, ConfigurationExposeGroup.DELETE] }),
    ValidateNested({ always: true }),
    IsOptional({ always: true }),
    __metadata("design:type", ReadDisabledActionsByChannelDto)
], ReadConfigurationDto.prototype, "disabledActions", void 0);
__decorate([
    ApiPropertyOptional({ type: () => String, description: 'list of allowed countries', example: 'us' }),
    Type(() => String),
    IsArray({ always: true }),
    Expose({ groups: [ConfigurationExposeGroup.FIXTURE, ConfigurationExposeGroup.CREATE, ConfigurationExposeGroup.READ, ConfigurationExposeGroup.DELETE] }),
    IsOptional({ always: true }),
    __metadata("design:type", Array)
], ReadConfigurationDto.prototype, "countryCodes", void 0);
__decorate([
    ApiPropertyOptional({
        oneOf: refs(ReadSmsSimpleTextingChannelConfigurationDto, ReadSmsNexmoChannelConfigurationDto, ReadSmsPlivoChannelConfigurationDto),
        discriminator: {
            propertyName: nameOf('provider'),
            mapping: {
                [NotifierProviderTypeEnum.SMS_SIMPLE_TEXTING]: getSchemaPath(ReadSmsSimpleTextingChannelConfigurationDto),
                [NotifierProviderTypeEnum.SMS_NEXMO]: getSchemaPath(ReadSmsNexmoChannelConfigurationDto),
                [NotifierProviderTypeEnum.SMS_PLIVO]: getSchemaPath(ReadSmsPlivoChannelConfigurationDto),
                [NotifierProviderTypeEnum.SMS_GOPARROT]: getSchemaPath(ReadSmsGoparrotChannelConfigurationDto),
            },
        },
    }),
    Type(() => ReadConfigurationProviderDto, {
        keepDiscriminatorProperty: true,
        discriminator: {
            property: nameOf('provider'),
            subTypes: [
                { value: ReadSmsSimpleTextingChannelConfigurationDto, name: NotifierProviderTypeEnum.SMS_SIMPLE_TEXTING },
                { value: ReadSmsNexmoChannelConfigurationDto, name: NotifierProviderTypeEnum.SMS_NEXMO },
                { value: ReadSmsPlivoChannelConfigurationDto, name: NotifierProviderTypeEnum.SMS_PLIVO },
                { value: ReadSmsGoparrotChannelConfigurationDto, name: NotifierProviderTypeEnum.SMS_GOPARROT },
            ],
        },
    }),
    Expose({
        groups: [
            ConfigurationExposeGroup.FIXTURE,
            ConfigurationExposeGroup.CREATE,
            ConfigurationExposeGroup.READ,
            ConfigurationExposeGroup.PATCH,
            ConfigurationExposeGroup.DELETE,
        ],
    }),
    ValidateNested({ always: true }),
    IsOptional({ always: true }),
    __metadata("design:type", Object)
], ReadConfigurationDto.prototype, "sms", void 0);
__decorate([
    ApiPropertyOptional({
        oneOf: refs(ReadMailMailhogChannelConfigurationDto, ReadMailPostMarkChannelConfigurationDto),
        discriminator: {
            propertyName: nameOf('provider'),
            mapping: {
                [NotifierProviderTypeEnum.MAIL_MAILHOG]: getSchemaPath(ReadMailMailhogChannelConfigurationDto),
                [NotifierProviderTypeEnum.MAIL_POST_MARK]: getSchemaPath(ReadMailPostMarkChannelConfigurationDto),
            },
        },
    }),
    Type(() => ReadConfigurationProviderDto, {
        keepDiscriminatorProperty: true,
        discriminator: {
            property: nameOf('provider'),
            subTypes: [
                { value: ReadMailMailhogChannelConfigurationDto, name: NotifierProviderTypeEnum.MAIL_MAILHOG },
                { value: ReadMailPostMarkChannelConfigurationDto, name: NotifierProviderTypeEnum.MAIL_POST_MARK },
            ],
        },
    }),
    Expose({
        groups: [
            ConfigurationExposeGroup.FIXTURE,
            ConfigurationExposeGroup.READ,
            ConfigurationExposeGroup.CREATE,
            ConfigurationExposeGroup.PATCH,
            ConfigurationExposeGroup.DELETE,
        ],
    }),
    IsOptional({ groups: [ConfigurationExposeGroup.CREATE, ConfigurationExposeGroup.PATCH] }),
    ValidateNested({ always: true }),
    IsOptional({ always: true }),
    __metadata("design:type", Object)
], ReadConfigurationDto.prototype, "mail", void 0);
ReadConfigurationDto = __decorate([
    ApiExtraModels(ReadOneSignalConfigurationDto, ReadSmsNexmoChannelConfigurationDto, ReadSmsPlivoChannelConfigurationDto, ReadSmsSimpleTextingChannelConfigurationDto, ReadSmsGoparrotChannelConfigurationDto, ReadMailMailhogChannelConfigurationDto, ReadMailPostMarkChannelConfigurationDto)
], ReadConfigurationDto);
export { ReadConfigurationDto };
//# sourceMappingURL=ReadConfigurationDto.js.map